import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { _HttpClient } from '@delon/theme';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { RecurringTransactions } from 'src/app/core/constants';
import { DateFormatService } from 'src/app/shared/services/date-format.service';

@Component({
  selector: 'app-recurring-transaction-modal',
  templateUrl: './recurring-transaction-modal.component.html',
  styleUrls: [],
  standalone: false
})
export class RecurringTransactionModalComponent implements OnInit {
  @Input() visible = false;
  @Input() isEdit = false;
  @Input() data: any = {}; // Generic object to hold modal data
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  form!: FormGroup;

  showEveryField = false;
  showNextRunDate = false;
  everyOptions: Array<{ value: any; label: string }> = [];
  daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  daysOfMonth = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${this.getOrdinalSuffix(i + 1)}`
  }));

  documentTypes = RecurringTransactions;

  recurrenceTypes = [
    { value: 0, label: 'Scheduled' },
    { value: 1, label: 'Unscheduled' },
    { value: 2, label: 'Reminder' }
  ];

  intervals = [
    { value: 0, label: 'Daily' },
    { value: 1, label: 'Weekly' },
    { value: 2, label: 'Monthly' },
    { value: 3, label: 'Yearly' }
  ];

  loading = false;

  dataSnapshot: any = {};

  constructor(private fb: FormBuilder,
    private http: _HttpClient,
    private router: Router,
    private notification: NzNotificationService,
    public dateFormatService: DateFormatService) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      templateName: [this.data.templateName || '', [Validators.required, Validators.maxLength(100)]],
      docType: [this.data.docType || '', [Validators.required]],
      recurrenceType: [this.data.recurrenceType || 0, [Validators.required]],
      interval: [this.data.interval || 0, [Validators.required]],
      startDate: [this.data.startDate || null],
      expiryDate: [this.data.expiryDate || null],
      amount: [this.data.amount || 0, [Validators.required, Validators.min(0)]],
      notes: [this.data.notes || ''],
      active: [this.data.active ?? true],
      every: [null], // Add the 'every' field here
      nextRunDate: [(this.data.interval == 3 ? this.data.nextDate : null) || null], // Add 'nextRunDate' if required for yearly interval
      createTransactionInAdvance: [null],
      daysToRemind: [null],
      id: [this.data.amount || 0]
    }, { validators: this.everyFieldValidator });

    this.handleRecurrenceTypeChange(this.form.value.recurrenceType);
  }

  private everyFieldValidator(form: FormGroup): { [key: string]: any } | null {
    const interval = form.get('interval')?.value;
    const every = form.get('every')?.value;
    const recurrenceType = form?.get('recurrenceType')?.value;
    const daysToRemind = form?.get('daysToRemind')?.value;

    // Check if interval is weekly (e.g., 1) or monthly (e.g., 2)
    if ((interval === 1 || interval === 2) && !every && every !== 0) {
      return { everyRequired: true }; // Validation fails if 'every' is empty
    }
    // Check if daysToRemind is required and missing
    if (recurrenceType === 2 && (daysToRemind === null || daysToRemind === undefined || daysToRemind === '')) {
      return { daysToRemindRequired: true }; // Validation fails
    }

    // Check if value exceeds the maximum limit
    if (daysToRemind > 999) {
      return { daysToRemindRequired: true }; // Validation fails
    }

    return null; // Validation passes
  }

  getDaysToRemindErrorMessage(): string {
    const control = this.form.get('daysToRemind');
    if (control?.hasError('required')) {
      return 'Days to Remind is required for reminder recurrence type.';
    }
    if (control?.hasError('max')) {
      return 'The maximum number of days to remind is 999.';
    }
    return '';
  }

  openEdit(template: any){
    this.dataSnapshot = JSON.parse(template.dataSnapshot);
    this.form.patchValue({
      templateName: template.templateName,
      docType: template.docType,
      recurrenceType: template.recurrenceType,
      interval: template.interval,
      startDate: template.startDate ? new Date(template.startDate) : null,
      expiryDate: template.expiryDate ? new Date(template.expiryDate) : null,
      amount: template.amount,
      notes: template.notes,
      every: template.every,
      nextRunDate: template.interval == 3 && template.nextDate ? new Date(template.nextDate) : null,
      createTransactionInAdvance: template.createTransactionInAdvance,
      daysToRemind: template.daysToRemind,
      active: template.active ?? true,
      id: template.id
    });

    this.handleRecurrenceTypeChange(this.form.value.recurrenceType);
    this.handleIntervalChange(template.interval, template.every);
    this.isEdit = true;
    this.visible = true;
  }

  getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return 'th'; // Covers 11th to 19th
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  handleIntervalChange(interval: number, every: number | null = null): void {
    this.form.get('every')?.reset(every);
    if (interval === 0) {
      // Daily
      this.showEveryField = false;
      this.showNextRunDate = false;
    } else if (interval === 1) {
      // Weekly
      this.everyOptions = this.daysOfWeek;
      this.showEveryField = true;
      this.showNextRunDate = false;
    } else if (interval === 2) {
      // Monthly
      this.everyOptions = this.daysOfMonth;
      this.showEveryField = true;
      this.showNextRunDate = false;
    } else if (interval === 3) {
      // Yearly
      this.showEveryField = false;
      this.showNextRunDate = true;
    }
  }

  handleRecurrenceTypeChange(recurrenceType: number): void {
    this.form.get('daysToRemind')?.setValidators(null);
    this.form.get('createTransactionInAdvance')?.enable();
    this.form.get('daysToRemind')?.enable();
    if (recurrenceType === 2) {
      this.form.get('createTransactionInAdvance')?.disable();
      // Reminder
      this.form.get('daysToRemind')?.setValidators([Validators.required]); // Make 'Days to Remind' required
    } else if (recurrenceType === 1) {
      this.form.get('createTransactionInAdvance')?.disable();
      this.form.get('daysToRemind')?.disable();
    }

    // Update form validation
    this.form.get('daysToRemind')?.updateValueAndValidity();
    this.form.get('createTransactionInAdvance')?.updateValueAndValidity();
  }

  handleDocTypeChange(docType: string){

  }

  openAdd(dType: string, data: any){
    var docType = this.documentTypes.find(p => p.aclKey === dType);
    if (docType) {
      this.form.controls['docType'].reset(docType.value);
    }else{
      return;
    }
    this.dataSnapshot = data;
    this.visible = true;
  }

  disablePastDates = (current: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return current < today;
  };

  closeModal(): void {
    this.visible= false;
  }

  saveTransaction(): void {
    if (this.form.invalid) {
      this.notification.error('Validation Error', 'Please fill out all required fields.');
      return;
    }

    this.loading = true; // Set loading state
    let payload = this.form.value;
    payload.customerId = this.dataSnapshot.customerId;
    payload.supplierId = this.dataSnapshot.supplierId;
    payload.payToFrom = this.dataSnapshot.payTo ?? this.dataSnapshot.receiveFrom ?? this.dataSnapshot.customerName;
    payload.amount = this.dataSnapshot.totalCredit ?? this.dataSnapshot.totalDebit ?? this.dataSnapshot.netTotalAmount ?? this.dataSnapshot.totalAmount;
    payload.dataSnapshot = JSON.stringify(this.dataSnapshot);
    const apiUrl = this.isEdit
      ? 'api/RecurringTransactions/Update'
      : 'api/RecurringTransactions/Create';

    // Call the API
    this.http.post(apiUrl, payload).subscribe({
      next: (response: any) => {
        this.notification.success(
          'Success',
          `Recurring Transaction Template ${this.isEdit ? 'updated' : 'created'} successfully!`
        );
        this.loading = false;
        this.save.emit(response);
      },
      error: (err) => {
        this.loading = false;
        this.notification.error(
          'Error',
          err?.error?.message || 'An error occurred while saving the template.'
        );
      },
      complete: () => {
        this.loading = false; // Reset loading state
      }
    });
  }

}
